import { App as AntdApp, Alert, Button, Spin, Switch } from 'antd';
import { SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ArrangementAuthoringSettingDto } from '@pams/shared';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { SETTINGS_COPY } from './copy';

/**
 * ─── SYSTEM-WIDE SETTINGS. [C1h] §1 ─────────────────────────────────────────────
 *
 * ⚠️ THE NAV ROW IS NOT THE GATE. `settings.write` is checked on the server, and
 * `RequireCapability` bounces the route — the brief's own sentence is that "a settings
 * screen that gates in the nav is not gated".
 *
 * [amended LIGHT-COLOURFUL] The Appearance section is gone. The colourful light palette is the
 * only light palette for everybody, so there is no longer a standard/colourful choice to save.
 */
const SettingsPage = () => {
  const { message } = AntdApp.useApp();
  const queryClient = useQueryClient();

  /*
   * ⚠️ [UX1 §8] THE PENDING EDIT, NOT THE VALUE — `null` MEANS "THE OPERATOR HAS NOT TOUCHED IT".
   * Never seeded from the query in an effect: a seeding effect is a cascading render
   * `eslint-plugin-react-hooks` refuses, and it races a refetch that then lands the server's copy
   * on top of what was just picked.
   */
  const nodalCreate = useQuery({
    queryKey: queryKeys.settings.arrangementAuthoring(),
    queryFn: ({ signal }) => api.get<ArrangementAuthoringSettingDto>('/settings/arrangement-authoring', { signal }),
  });
  const [nodalChoice, setNodalChoice] = useState<boolean | null>(null);
  const nodalValue = nodalChoice ?? nodalCreate.data?.nodalsMayAuthorArrangements ?? null;

  const saveNodal = useMutation({
    mutationFn: (nodalsMayAuthorArrangements: boolean) =>
      api.put<ArrangementAuthoringSettingDto>('/settings/arrangement-authoring', { nodalsMayAuthorArrangements }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.settings.arrangementAuthoring(), data);
      /* ⚠️ The nav action and the route gate read this key through their own hook, so the
         operator who threw the switch sees it without a reload. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings.arrangementAuthoring() });
      message.success(SETTINGS_COPY.arrangementsSaved);
    },
  });

  const nodalDirty =
    nodalValue !== null &&
    nodalCreate.data !== undefined &&
    nodalValue !== nodalCreate.data.nodalsMayAuthorArrangements;

  return (
    <div className="flex flex-col gap-6" data-testid="settings-root">
      <PageHeader icon={SlidersHorizontal} title={SETTINGS_COPY.title} subtitle={SETTINGS_COPY.subtitle} />

      {/*
        ⚠️ [UX1 §8] NO RELOAD ON SUCCESS. A capability is read by a live query — the nav action
        and the route gate — so the operator who threw the switch sees it on their next fetch and
        everybody else on their next load, which is what the hint says.
      */}
      <section className="rounded-lg border border-border bg-card p-5" aria-labelledby="settings-arrangements">
        <h2 id="settings-arrangements" className="text-base font-medium text-heading">
          {SETTINGS_COPY.arrangements}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{SETTINGS_COPY.arrangementsHint}</p>

        {nodalCreate.isPending || nodalValue === null ? (
          <div className="py-6">
            <Spin />
          </div>
        ) : (
          <>
            <label className="mt-4 flex items-center gap-3">
              <Switch
                checked={nodalValue}
                data-testid="nodal-create-choice"
                onChange={(next) => {
                  setNodalChoice(next);
                }}
              />
              <span className="text-sm">
                {nodalValue ? SETTINGS_COPY.arrangementsOn : SETTINGS_COPY.arrangementsOff}
              </span>
            </label>

            {/* ⚠️ §8a — shown only when the operator is about to REMOVE the ability. */}
            {nodalCreate.data?.nodalsMayAuthorArrangements === true && nodalValue === false ? (
              <Alert className="mt-3" type="warning" showIcon message={SETTINGS_COPY.arrangementsWarning} />
            ) : null}

            <Button
              className="mt-4"
              type="primary"
              disabled={!nodalDirty}
              loading={saveNodal.isPending}
              onClick={() => {
                if (nodalValue !== null) saveNodal.mutate(nodalValue);
              }}
              data-testid="nodal-create-save"
            >
              {SETTINGS_COPY.saveArrangements}
            </Button>
          </>
        )}
      </section>
    </div>
  );
};

export default SettingsPage;
